import React, { useState, useEffect } from 'react';
import axios from 'axios';
axios.defaults.withCredentials = true;
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Settings, Plus, Trash2, Loader2, Power } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ppn_enabled: true,
    ppn_rate: 1.0,
    dp_type: 'percent',
    dp_percent: 30.0,
    dp_value: 0.0,
    site_name: 'layanan.umroh',
    contact_phone_1: '',
    contact_phone_2: '',
    address: '',
    city: 'Tangerang',
    email: '',
    instagram: '',
    bank_accounts: [{ bank_name: '', account_number: '', account_name: '', is_active: true }]
  });
  const [vouchers, setVouchers] = useState([]);
  const [newVoucher, setNewVoucher] = useState({ code: '', type: 'percent', value: 0, min_order: 0, active: true, expires_at: '' });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/settings/`);
        setForm(prev => ({ ...prev, ...res.data }));
      } catch (err) {
        toast.error('Gagal memuat pengaturan');
      } finally {
        setLoading(false);
      }
    };
    const fetchVouchers = async () => {
      try {
        const res = await axios.get(`${API}/vouchers/`);
        setVouchers(res.data);
      } catch {}
    };
    fetch();
    fetchVouchers();
  }, []);

  const addVoucher = async () => {
    if (!newVoucher.code.trim()) { toast.error('Kode voucher wajib diisi'); return; }
    try {
      const payload = { ...newVoucher, code: newVoucher.code.trim().toUpperCase(), value: parseFloat(newVoucher.value) || 0, min_order: parseFloat(newVoucher.min_order) || 0 };
      if (!payload.expires_at) delete payload.expires_at;
      await axios.post(`${API}/vouchers/`, payload);
      toast.success('Voucher ditambahkan');
      setNewVoucher({ code: '', type: 'percent', value: 0, min_order: 0, active: true, expires_at: '' });
      const res = await axios.get(`${API}/vouchers/`);
      setVouchers(res.data);
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deleteVoucher = async (code) => {
    try {
      await axios.delete(`${API}/vouchers/${code}`);
      toast.success('Voucher dihapus');
      setVouchers(prev => prev.filter(v => v.code !== code));
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleBankChange = (index, field, value) => {
    const updated = [...form.bank_accounts];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, bank_accounts: updated }));
  };

  const addBank = () => {
    setForm(prev => ({
      ...prev,
      bank_accounts: [...prev.bank_accounts, { bank_name: '', account_number: '', account_name: '', is_active: true }]
    }));
  };

  const removeBank = (index) => {
    if (form.bank_accounts.length <= 1) return;
    setForm(prev => ({
      ...prev,
      bank_accounts: prev.bank_accounts.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.ppn_rate = parseFloat(payload.ppn_rate) || 1.0;
      payload.dp_percent = parseFloat(payload.dp_percent) || 0;
      payload.dp_value = parseFloat(payload.dp_value) || 0;
      await axios.put(`${API}/settings/`, payload);
      toast.success('Pengaturan berhasil disimpan');
    } catch (err) {
      toast.error('Gagal menyimpan: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Memuat pengaturan...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Pengaturan</h1>
        <Button onClick={handleSave} disabled={saving} className="rounded-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Simpan
        </Button>
      </div>

      <div className="space-y-6">
        {/* PPN */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Pengaturan PPN</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between max-w-xs">
              <div>
                <Label>PPN Aktif</Label>
                <p className="text-xs text-muted-foreground">Nonaktifkan untuk menonaktifkan PPN</p>
              </div>
              <button
                type="button"
                onClick={() => handleChange('ppn_enabled', !form.ppn_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.ppn_enabled ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.ppn_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="space-y-2 max-w-xs">
              <Label>Tarif PPN (%)</Label>
              <Input type="number" step="0.1" min="0" max="100"
                value={form.ppn_rate} onChange={(e) => handleChange('ppn_rate', e.target.value)}
                disabled={!form.ppn_enabled}
                className={!form.ppn_enabled ? 'opacity-50' : ''} />
              <p className="text-xs text-muted-foreground">
                {form.ppn_enabled ? 'Digunakan dalam perhitungan total pembayaran' : 'PPN dinonaktifkan — tidak ada pajak'}
              </p>
            </div>
            <div className="space-y-2 max-w-md">
              <Label>Nama Situs</Label>
              <Input value={form.site_name} onChange={(e) => handleChange('site_name', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Info Kontak */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Informasi Kontak</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>No. Telepon 1</Label>
                <Input value={form.contact_phone_1} onChange={(e) => handleChange('contact_phone_1', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>No. Telepon 2</Label>
                <Input value={form.contact_phone_2} onChange={(e) => handleChange('contact_phone_2', e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Alamat</Label>
              <Input value={form.address} onChange={(e) => handleChange('address', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Kota</Label>
              <Input value={form.city} onChange={(e) => handleChange('city', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="admin@layananumroh.com" />
            </div>
            <div className="space-y-2">
              <Label>Instagram</Label>
              <Input value={form.instagram} onChange={(e) => handleChange('instagram', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Voucher & DP */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Voucher &amp; DP</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 max-w-xs">
              <Label>Tipe DP</Label>
              <select value={form.dp_type} onChange={(e) => handleChange('dp_type', e.target.value)}
                className="w-full rounded-lg border border-border p-2 bg-background">
                <option value="percent">Persen (%)</option>
                <option value="nominal">Nominal (Rp)</option>
              </select>
            </div>
            {form.dp_type === 'percent' ? (
              <div className="space-y-2 max-w-xs">
                <Label>Persen DP (%)</Label>
                <Input type="number" step="1" min="0" max="100"
                  value={form.dp_percent} onChange={(e) => handleChange('dp_percent', e.target.value)} />
              </div>
            ) : (
              <div className="space-y-2 max-w-xs">
                <Label>Nominal DP (Rp)</Label>
                <Input type="number" step="1000" min="0"
                  value={form.dp_value} onChange={(e) => handleChange('dp_value', e.target.value)} />
              </div>
            )}
            <p className="text-xs text-muted-foreground">DP dibayar di awal, sisa menjadi pelunasan</p>

            <div className="border-t pt-4">
              <Label className="mb-2 block">Daftar Voucher</Label>
              <div className="space-y-2">
                {vouchers.length === 0 && <p className="text-sm text-muted-foreground">Belum ada voucher</p>}
                {vouchers.map((v, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-xl">
                    <div>
                      <p className="font-medium">{v.code}</p>
                      <p className="text-xs text-muted-foreground">
                        {v.type === 'percent' ? `${v.value}%` : `Rp ${Number(v.value).toLocaleString('id-ID')}`}
                        {v.min_order > 0 ? ` · min Rp ${Number(v.min_order).toLocaleString('id-ID')}` : ''}
                        {!v.active ? ' · nonaktif' : ''}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteVoucher(v.code)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kode Voucher</Label>
                <Input value={newVoucher.code} onChange={(e) => setNewVoucher(prev => ({ ...prev, code: e.target.value }))} placeholder="Contoh: LEBARAN10" />
              </div>
              <div className="space-y-2">
                <Label>Tipe</Label>
                <select value={newVoucher.type} onChange={(e) => setNewVoucher(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full rounded-lg border border-border p-2 bg-background">
                  <option value="percent">Persen (%)</option>
                  <option value="nominal">Nominal (Rp)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{newVoucher.type === 'percent' ? 'Nilai (%)' : 'Nilai (Rp)'}</Label>
                <Input type="number" value={newVoucher.value} onChange={(e) => setNewVoucher(prev => ({ ...prev, value: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Min. Order (Rp, 0 = bebas)</Label>
                <Input type="number" value={newVoucher.min_order} onChange={(e) => setNewVoucher(prev => ({ ...prev, min_order: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Expired (opsional, YYYY-MM-DD)</Label>
                <Input type="date" value={newVoucher.expires_at} onChange={(e) => setNewVoucher(prev => ({ ...prev, expires_at: e.target.value }))} />
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" onClick={addVoucher} className="rounded-full w-full">
                  <Plus className="h-4 w-4 mr-1" /> Tambah Voucher
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rekening Bank */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Rekening Pembayaran</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.bank_accounts.map((bank, i) => (
              <div key={i} className="p-4 border rounded-xl space-y-3 relative">
                <Button variant="ghost" size="sm" className="absolute top-2 right-2 text-destructive"
                  onClick={() => removeBank(i)} disabled={form.bank_accounts.length <= 1}>
                  <Trash2 className="h-4 w-4" />
                </Button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Nama Bank</Label>
                    <Input value={bank.bank_name} onChange={(e) => handleBankChange(i, 'bank_name', e.target.value)}
                      placeholder="BSI / BRI / Mandiri" />
                  </div>
                  <div className="space-y-2">
                    <Label>No. Rekening</Label>
                    <Input value={bank.account_number} onChange={(e) => handleBankChange(i, 'account_number', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Atas Nama</Label>
                    <Input value={bank.account_name} onChange={(e) => handleBankChange(i, 'account_name', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addBank} className="rounded-full">
              <Plus className="h-4 w-4 mr-1" /> Tambah Rekening
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsManagement;
